import { currentSnackbarState } from '@databases/snackbar/snackbar';
import { type Database, DatabaseFieldType } from '@databases/types/database';
import { generateAllValues } from '@databases/utils/database/utils';
import { useEffect } from 'react';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { safeGet, safeSet } from '@canvas/idb-cache';
import { isOmittedMediaIdPlaceholder } from '@canvas/media-id';

const NOTICE_KEY_PREFIX = 'brie-omitted-media-notice';
const MESSAGE_KEY = 'database.snackbar.omitted-media-files.non-final';

const noticeKey = (contentId: string) => `${NOTICE_KEY_PREFIX}:${contentId}`;

export function hasOmittedMedia(database: Database): boolean {
  for (const { value } of generateAllValues(database)) {
    if (value.type !== DatabaseFieldType.ATTACHMENT) {
      continue;
    }

    for (const attachment of value.value) {
      if (
        attachment &&
        typeof attachment === 'object' &&
        'fileId' in attachment &&
        isOmittedMediaIdPlaceholder(attachment.fileId)
      ) {
        return true;
      }
    }
  }

  return false;
}

export function useOmittedMediaNotice({
  contentId,
  database,
  isLoaded
}: {
  contentId: string;
  database: Database | undefined;
  isLoaded: boolean;
}) {
  useEffect(() => {
    if (
      !isFeatureFlagEnabled('uiBrieImprovements') ||
      !isLoaded ||
      !database ||
      !hasOmittedMedia(database)
    ) {
      return;
    }

    let active = true;

    const showNotice = async () => {
      const key = noticeKey(contentId);
      const alreadyShown = await safeGet<boolean>(key, { fallback: false });
      if (!active || alreadyShown) {
        return;
      }

      currentSnackbarState.setMessage(MESSAGE_KEY, {
        id: key,
        i18nVariables: { contentType: 'database' },
        showCloseButton: true
      });

      await safeSet(key, true);
    };

    void showNotice();

    return () => {
      active = false;
    };
  }, [contentId, database, isLoaded]);
}
